import { WellCorrelationWidget as WellCorrelationWidgetComponent, useBitmapFont } from '@go-widgets/well-logs-widget';
import inter18ptRegularFntUrl from '@go-widgets/well-logs-widget/dist/assets/fonts/Inter_18pt-Regular.fnt?url';
import inter18ptRegularPngUrl from '@go-widgets/well-logs-widget/dist/assets/fonts/Inter_18pt-Regular.png?url';
import { EditLightBig, FullArrowLight as FullIcon, ReduceLight as MinimizeIcon, VerticalCustom } from '@repo/go-icons';
import { ActionsBar, Button, ConfigContext, Drawer, LoaderIcon, Tooltip } from '@repo/go-ui';
import {
  parseVersionedReferenceWellsSettings,
  ReferenceDataTemplatesStore,
  ReferenceWellsChildrenStore,
  ReferenceWellsPanel,
  ReferenceWellsService,
  ReferenceWellsStore,
} from '@repo/reference-wells';
import { hasValue } from '@repo/utils';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from 'src/app/i18n';
import { requireService, useService } from 'src/packages/di';
import { ReferenceDataTemplatesApi } from 'src/packages/reference-data-templates-service/reference-data-templates-api';
import { ReferenceGroupFavoritesAdapter } from 'src/packages/reference-wells-favorites/reference-group-favorites-adapter';
import { createEnrichedReferenceWellsFullInfoApi } from 'src/packages/reference-wells-full-info-service/create-enriched-reference-wells-full-info-api';
import { ReferenceWellsFullInfoApi } from 'src/packages/reference-wells-full-info-service/reference-wells-full-info-api';
import { createReferenceWellsFiltersHostBridge } from 'src/packages/reference-wells-persistence/create-reference-wells-filters-host-bridge';
import { takePendingReferenceWellsTree } from 'src/packages/reference-wells-persistence/reference-wells-pending-tree-registry';
import { IS_PORTABLE_DEVICE } from 'src/packages/shared/consts/isPortableDevice';

import { NoWellChosen } from '../no-well-chosen';
import { WidgetHeader } from '../widget-header/WidgetHeader';
import type { TWidgetRendererOptions } from '../workspace/services/WidgetRendererService';

import type { WellCorrelationWidgetEntity } from './WellCorrelationWidget.entity';
import styles from './WellCorrelationWidget.module.scss';
import { WellCorrelationWidgetStore } from './WellCorrelationWidget.store';

type WrapperProps = {
  entity: WellCorrelationWidgetEntity;
  options: TWidgetRendererOptions;
};

type Props = {
  store: WellCorrelationWidgetStore;
  entity: WellCorrelationWidgetEntity;
  options: TWidgetRendererOptions;
};

const CORRELATION_REFERENCE_WELLS_VISIBILITY = {
  showFact: true,
  showPlan: false,
  showTrajectories: false,
};

export const WellCorrelationWidgetWrapper = observer(function WellCorrelationWidgetWrapper({
  entity,
  options,
}: WrapperProps) {
  const [store, setStore] = useState<WellCorrelationWidgetStore | null>(null);
  const storeService = useService('widgetStoreService');

  useEffect(() => {
    if (store) return;

    const existing = storeService.getStore<WellCorrelationWidgetEntity, WellCorrelationWidgetStore>(entity);

    if (existing) {
      existing.referenceWellsStore.setFiltersHostBridge(
        createReferenceWellsFiltersHostBridge({
          getGroupId: () => entity.groupId,
          groupSelectStore: options.groupSelectStore,
        }),
      );
      setStore(existing);
    } else {
      const pendingTree = takePendingReferenceWellsTree(entity.uuid);
      const initialSettings = pendingTree !== undefined ? parseVersionedReferenceWellsSettings(pendingTree) : undefined;

      const referenceWellsService = new ReferenceWellsService({
        wellsSource: requireService('wellsData'),
        primaryWellId: entity.wellId,
        initialSelectedTree: initialSettings?.tree,
        visibility: CORRELATION_REFERENCE_WELLS_VISIBILITY,
      });
      const referenceWellsStore = new ReferenceWellsStore(referenceWellsService, {
        initialFilters: initialSettings?.filters ?? null,
        persistedSettingsToRestore: entity.restoredFromTabSnapshot ? initialSettings : undefined,
        filtersHostBridge: createReferenceWellsFiltersHostBridge({
          getGroupId: () => entity.groupId,
          groupSelectStore: options.groupSelectStore,
        }),
      });
      const referenceDataTemplatesStore = new ReferenceDataTemplatesStore({
        api: new ReferenceDataTemplatesApi(),
        wellsService: referenceWellsService,
        getOwnerUserId: () => requireService('authService').userInfo.sub,
        getDraftTree: () => referenceWellsStore.selectedTree,
        getDraftFilters: () => referenceWellsStore.filters,
        applyReferenceWellsSettings: (settings) => referenceWellsStore.applySettingsFromGroup(settings),
        onCommittedSelection: () => referenceWellsStore.syncDraftFromApplied(),
        groupFavorites: new ReferenceGroupFavoritesAdapter(requireService('userPreference')),
      });
      const referenceWellsChildrenStore = new ReferenceWellsChildrenStore({
        api: createEnrichedReferenceWellsFullInfoApi(new ReferenceWellsFullInfoApi()),
        referenceWellsStore,
      });

      const newStore = new WellCorrelationWidgetStore(entity, options.groupSelectStore, options.wellListEntity, {
        referenceWellsService,
        referenceWellsStore,
        referenceDataTemplatesStore,
        referenceWellsChildrenStore,
      });
      storeService.setStore<WellCorrelationWidgetEntity, WellCorrelationWidgetStore>(entity, newStore);
      setStore(newStore);
    }
  }, [setStore, storeService, entity, options.groupSelectStore, store, options.wellListEntity]);

  if (!store) return null;

  return <WellCorrelationWidget store={store} entity={entity} options={options} />;
});

export const WellCorrelationWidget = observer(function WellCorrelationWidget({ entity, options, store }: Props) {
  const { setGroupForWellCorrelation, exitFullScreen, isReady, effect, enableEditMode } = store;
  const { t } = useTranslation(['common', 'widgets']);
  const globalConfig = useContext(ConfigContext);
  const language = useService('language');
  const [widgetRoot, setWidgetRoot] = useState<HTMLDivElement | null>(null);

  const { wellId, isFullscreen, state, setFullscreen, wellboreName, isEditMode } = entity;

  const { loaded: bitmapFontLoaded, error: bitmapFontError } = useBitmapFont({
    fontName: 'Inter_18pt-Regular',
    fntUrl: inter18ptRegularFntUrl,
    pngUrl: inter18ptRegularPngUrl,
  });

  useEffect(effect, [effect]);

  const directories = useService('directories');
  const wells = directories.getObject('GOdashboard_Well');
  const wellName = wells?.find((item) => item.id === wellId)?.data.name ?? wellId;
  const primaryWellName = hasValue(wellName) ? String(wellName) : null;

  const isWidgetContentReady = !!state && bitmapFontLoaded && !bitmapFontError;

  return (
    <div className={styles.wellCorrelationWidgetWrapper}>
      <WidgetHeader
        entity={entity}
        onWidgetChangeGroup={setGroupForWellCorrelation}
        groupSelectStore={options.groupSelectStore}
        widgetName={entity.title}
        wellName={wellName}
        wellboreName={wellboreName}
        onWidgetDelete={() => {
          options.onWidgetDelete(entity);
        }}
        showCloseButton={!isFullscreen}
        draggable={!isFullscreen}
        renderAdditionalButtons={() => {
          if (!isReady) return null;

          return (
            <>
              {!isEditMode && !IS_PORTABLE_DEVICE && (
                <Tooltip title={t('common:Widget.edit')} delay={400}>
                  <Button
                    onClick={enableEditMode}
                    variant="flat"
                    icon={<EditLightBig className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
              {!isFullscreen && (
                <Tooltip title={t('common:Widget.fullscreen')} delay={400}>
                  <Button
                    onClick={() => setFullscreen(true)}
                    variant="flat"
                    icon={<FullIcon className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
              {isFullscreen && (
                <Tooltip title={t('common:Widget.collapse')} delay={400}>
                  <Button
                    onClick={() => exitFullScreen(globalConfig)}
                    variant="flat"
                    icon={<MinimizeIcon className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
            </>
          );
        }}
      />

      {isWidgetContentReady ? (
        <div className={styles.wrapper}>
          <div ref={setWidgetRoot} className={styles.correlationRoot}>
            <WellCorrelationWidgetComponent
              widgetState={state}
              extraActionsBarItems={
                <Tooltip placement="left" title={t('widgets:WellCorrelation.referenceWells')} delay={400}>
                  <ActionsBar.Item active={store.isReferenceWellsPanelOpen} onClick={store.toggleReferenceWellsPanel}>
                    <VerticalCustom />
                  </ActionsBar.Item>
                </Tooltip>
              }
            />
            <Drawer
              title={t('widgets:WellCorrelation.referenceWells')}
              open={store.isReferenceWellsPanelOpen}
              onClose={() => store.setReferenceWellsPanelOpen(false)}
              container={widgetRoot ?? undefined}
            >
              <ReferenceWellsPanel
                store={store.referenceWellsStore}
                templatesStore={store.referenceDataTemplatesStore}
                language={language.language}
                parentI18n={i18n}
                primaryWellName={primaryWellName}
                showHeader={false}
              />
            </Drawer>
          </div>
        </div>
      ) : state ? (
        <div className={styles.wrapper}>
          <LoaderIcon />
        </div>
      ) : (
        <NoWellChosen />
      )}
    </div>
  );
});
